/**
 * What the panel exports (TZ 8.9.2): the screen's filters in panel terms, converted once into the backend
 * `ExportParameters` (docs/API.md `POST /exports`). Reports are rendered in the user's interface language.
 */
import type {
  ExportKind,
  ExportParams,
  ExportReport,
  ExportRequest,
  MetricCompareMode,
  MetricGrain,
  MetricKey,
  StoredExportParams,
  TradingDate,
} from '../../api/api_types';
import { METRIC_KEYS } from '../../api/api_constants';
import type { StoreScope } from '../../api/client_endpoints';
import type { ControlExportFilters } from '../../api/control_types';

export interface ExportFilters {
  from: TradingDate;
  to: TradingDate;
  stores: StoreScope;
  metrics?: readonly MetricKey[];
  grain?: MetricGrain;
  compare?: MetricCompareMode;
  /** Store control event filters (report "control_events" only; sent as `params.control`). */
  control?: ControlExportFilters;
}

export interface ExportOrder {
  kind: ExportKind;
  report: ExportReport;
  filters: ExportFilters;
}

export type ExportLanguage = NonNullable<ExportParams['language']>;
const EXPORT_LANGUAGES: readonly ExportLanguage[] = ['uz', 'ru', 'en'];
const DEFAULT_EXPORT_LANGUAGE: ExportLanguage = 'uz';

export function exportLanguageOf(interfaceLanguage: string): ExportLanguage {
  return (
    EXPORT_LANGUAGES.find((language) => interfaceLanguage.startsWith(language)) ?? DEFAULT_EXPORT_LANGUAGE
  );
}

export function toExportParameters(filters: ExportFilters, language: ExportLanguage): ExportParams {
  return {
    date_from: filters.from,
    date_to: filters.to,
    store_ids: filters.stores === 'all' ? null : [...filters.stores],
    metrics: filters.metrics === undefined ? [] : [...filters.metrics],
    grain: filters.grain ?? 'day',
    compare: filters.compare ?? 'none',
    language,
  };
}

export function toExportRequest(exportOrder: ExportOrder, language: ExportLanguage): ExportRequest {
  const parameters = toExportParameters(exportOrder.filters, language);
  return {
    kind: exportOrder.kind,
    report: exportOrder.report,
    params:
      exportOrder.filters.control === undefined
        ? parameters
        : { ...parameters, control: exportOrder.filters.control },
  };
}

/** Filters of a saved report template (its stored `ExportParameters`); a template without dates covers `today`. */
export function exportFiltersOf(parameters: StoredExportParams, today: TradingDate): ExportFilters {
  return {
    from: parameters.date_from ?? today,
    to: parameters.date_to ?? today,
    stores: parameters.store_ids ?? 'all',
    metrics: (parameters.metrics ?? []).filter(isMetricKey),
    grain: parameters.grain,
    compare: parameters.compare,
  };
}

function isMetricKey(candidate: string): candidate is MetricKey {
  return (METRIC_KEYS as readonly string[]).includes(candidate);
}
